import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import {
  FDBox,
  FDTextButton,
  FDStack,
  Tabs,
  FDBodyText,
  FDHeadlineText,
  FDAlert,
  FDSecondaryActionButton,
} from '@facet-wealth/design';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import FacetPlanNextUpScenarioReviewLayout from 'Views/Page/Content/Templates/FacetPlanProposalNextUpReviewPageTemplate/FacetPlanNextUpScenarioReview';
import { useSnackbarContext } from 'Context/SnackbarContext';
import SendProposalButtonWithModal
  from 'Views/Page/Content/Templates/FacetPlanProposalNextUpReviewPageTemplate/SendProposalButtonWithModal/SendProposalButtonWithModal';
import { dataAttrs } from 'Helpers/Testing';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';

export default function FacetPlanProposalNextUpReviewPageTemplate({
  resource,
}) {
  const {
    loadPageContentLink,
  } = usePageContentContext();
  const [confirmationDialogOpen, setConfirmationDialogOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isNavigatingBack, setIsNavigatingBack] = React.useState(false);
  const [isLoadingRoadmapDeliverable, setIsLoadingRoadmapDeliverable] = React.useState(false);
  const { displayErrorMessage } = useSnackbarContext();

  const firstTabId = resource.attr('scenarioReviews').resources().head()?.id();
  const [selectedTab, setSelectedTab] = React.useState(firstTabId);
  const [reviewedTabs, setReviewedTabs] = React.useState(new Set([firstTabId]));

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const coll = await resource.submit();
      const nextUpPageResource = coll?.resources()?.head();

      if (nextUpPageResource && nextUpPageResource?.relationship(fr.relationships.proposalPreviewPage.name)?.link('related')) {
        await loadPageContentLink(nextUpPageResource.relationship(fr.relationships.proposalPreviewPage.name).link('related'));
      }

      setConfirmationDialogOpen(false);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = async (rel) => {
    setIsNavigatingBack(true);
    try {
      await loadPageContentLink(rel.link('related'));
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setIsNavigatingBack(false);
    }
  };

  const onRoadmapDeliverableClick = async (rel) => {
    setIsLoadingRoadmapDeliverable(true);
    try {
      await loadPageContentLink(rel.link('related'));
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to load roadmap deliverable');
    } finally {
      setIsLoadingRoadmapDeliverable(false);
    }
  };

  const reviewedAllTabs = reviewedTabs.size === resource.attr('scenarioReviews').resources().length();

  return (
    <React.Fragment>
      <FDBox padding={300} {...dataAttrs('proposal-next-up-review-page')}>
        <FDStack direction="column" spacing={1} alignItems="flex-start" justifyContent="flex-start">
          {resource.withRelationship(fr.relationships.back.name, (rel) => (
            <FDTextButton
              icon="chevronLeft"
              label={rel.meta('prompt')}
              loading={isNavigatingBack}
              disabled={isNavigatingBack}
              onClick={() => handleBack(rel)}
            />
          ),
          () => null)}
          <FDBox width="100%" padding={300}>
            <FDStack alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <FDStack direction="column" spacing={1} alignItems="flex-start" justifyContent="flex-start">
                <FDHeadlineText value={resource.attr('name') ?? ''} />
                <FDBodyText value={resource.attr('description') ?? ''} />
              </FDStack>
              <FDStack alignItems="flex-end" justifyContent="flex-end" direction="column" spacing={1}>
                <FDStack direction={'row'} spacing={1}>
                  {resource.withRelationship(fr.relationships.roadmapDeliverable.name, (rel) => (
                    <FDSecondaryActionButton
                      label={rel.meta('prompt')}
                      onClick={() => onRoadmapDeliverableClick(rel)}
                      loading={isLoadingRoadmapDeliverable}
                      {...dataAttrs('roadmap-deliverable-preview-button')}
                    />
                  ),
                  () => null)}
                  <SendProposalButtonWithModal
                    resource={resource}
                    loading={isSubmitting}
                    disabled={!resource.meta().canPost() || isSubmitting || !reviewedAllTabs}
                    onSubmit={handleSubmit}
                    open={confirmationDialogOpen}
                    onClick={() => {
                      setConfirmationDialogOpen(true);
                    }}
                    onClose={() => {
                      setConfirmationDialogOpen(false);
                    }}
                  />
                </FDStack>
                {!reviewedAllTabs && <FDAlert status="info" title={'You must review all scenarios before sending to member'} />}
              </FDStack>
            </FDStack>
          </FDBox>
          <Tabs.Horizontal
            onChange={(_event, tabValue) => {
              setSelectedTab(tabValue);
              setReviewedTabs(prev => prev.add(tabValue));
            }}
            options={resource.attr('scenarioReviews').resources().list().map((el) => {
              return ({
                value: el.id(),
                label: el.attr('title'),
              });
            })}
            value={selectedTab}
          />
          <FDBox width="100%">
            {resource.attr('scenarioReviews').resources().list().map(el => (
              <Tabs.Panel key={el.id()} id={el.id()} value={selectedTab}>
                <FDBox height={'65vh'}>
                  <FacetPlanNextUpScenarioReviewLayout resource={el} />
                </FDBox>
              </Tabs.Panel>
            ))}
          </FDBox>
        </FDStack>
      </FDBox>
    </React.Fragment>
  );
}

FacetPlanProposalNextUpReviewPageTemplate.defaultProps = {
};

FacetPlanProposalNextUpReviewPageTemplate.propTypes = {
  resource: PropTypes.object.isRequired,
};
